import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import Tooltipable from "~/components/effects/tooltipable";
import MODEL_TIERS, { TIER_STORAGE_KEY } from "~/constants/ModelTiers";
import { captureEvent } from "~/helpers/capture_posthog_event";

const ModelTierSelector = ({
  selectedTier,
  onTierChange,
  conversationId,
  isTemplateSet = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selected =
    MODEL_TIERS.find((t) => t.value === selectedTier) || MODEL_TIERS[0];
  const tooltipContent = (
    <div className="text-left" style={{ maxWidth: 280 }}>
      <strong>AI Model</strong>
      <hr className="my-1 border-light" />
      {MODEL_TIERS.map((tier, idx) => (
        <div key={tier.value} className={idx > 0 ? "mt-2" : undefined}>
          <div>
            <FontAwesomeIcon icon={tier.icon} className="mr-1" />
            <strong>{tier.label}</strong>
            {tier.value === selected.value && (
              <span className="ml-1 text-success">(current)</span>
            )}
            <span className="ml-1 text-muted">· {tier.latency}</span>
          </div>
          <div className="small">
            <em>{tier.bestFor}.</em> {tier.description}
          </div>
        </div>
      ))}
    </div>
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelect = (tier) => {
    if (tier.value !== selectedTier) {
      captureEvent("ai_model_tier_selected", {
        tier: tier.value,
        previous_tier: selectedTier,
        conversation_id: conversationId,
      });
      // BK-3229 R4 AC 3.1.3 — manual override during a template-driven
      // generation does not update browser memory; the freeform default
      // stays untouched. When no template is active, persist as before.
      if (!isTemplateSet) {
        localStorage.setItem(TIER_STORAGE_KEY, tier.value);
      }
      onTierChange(tier.value);
    }
    setIsOpen(false);
  };

  return (
    <div
      ref={dropdownRef}
      className="d-flex align-items-center flex-nowrap position-relative"
    >
      <button
        type="button"
        className="btn btn-sm btn-outline-success rounded-pill text-nowrap text-sm py-0 px-2"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <FontAwesomeIcon icon={selected.icon} className="mr-1" />
        AI Model
        <FontAwesomeIcon
          icon={["fas", "fa-caret-down"]}
          className="ml-1 text-xs"
        />
      </button>

      <Tooltipable trigger="click" placement="right" text={tooltipContent}>
        <FontAwesomeIcon
          icon={["fal", "fa-info-circle"]}
          className="ml-2 text-secondary text-xs cursor-pointer"
        />
      </Tooltipable>

      {isOpen && (
        <div
          role="listbox"
          aria-label="Model tier selection"
          className="dropdown-menu show shadow-sm model-tier-dropdown"
        >
          {MODEL_TIERS.map((tier) => (
            // `<button>` gives us native Enter/Space activation + focus ring
            // without the bespoke keydown handler. `role="option"` keeps the
            // listbox/option semantics the surrounding wrapper announces.
            // Bootstrap's `.dropdown-item` already sets width/border/bg and
            // drives the hover/active styling — no extra helpers needed.
            <button
              key={tier.value}
              type="button"
              role="option"
              aria-selected={tier.value === selectedTier}
              className={`dropdown-item px-3 py-2 ${tier.value === selectedTier ? "bg-light" : ""}`}
              onClick={() => handleSelect(tier)}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  {tier.value === selectedTier ? (
                    <FontAwesomeIcon
                      icon="far fa-check"
                      className="mr-2 text-success fa-fw"
                    />
                  ) : (
                    <span className="mr-2 fa-fw d-inline-block" />
                  )}
                  <FontAwesomeIcon icon={tier.icon} className="mr-1" />
                  {tier.label}
                </div>
                <small className="ml-2 tier-latency">{tier.latency}</small>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

ModelTierSelector.propTypes = {
  selectedTier: PropTypes.string.isRequired,
  onTierChange: PropTypes.func.isRequired,
  conversationId: PropTypes.number,
  isTemplateSet: PropTypes.bool,
};

export default ModelTierSelector;
